import { useState } from "react";
import {
  Pencil,
  Sparkles,
  ChevronDown,
  Check,
  CircleDashed,
  CircleHelp,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Badge } from "@/modern/components/ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/modern/components/ui/collapsible";
import { cn } from "@/modern/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/modern/components/ui/tooltip";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { useCoverage } from "../context/CoverageContext";
import {
  problemHubStatusBadgeClass,
  sequenceBadgeClassFromStatus,
  SEQUENCE_STATUS_LABELS,
  SEQUENCE_STATUS_TOOLTIPS,
  BUCKET_STATUS_LABELS,
  BUCKET_STATUS_TOOLTIPS,
  TEMPORAL_PHASES,
} from "../constants";
import type { BucketFill, BucketStatus } from "../types";



const StatusBadgeInline = ({ status, label }: { status: BucketStatus; label: string }) => (
  <Badge variant="secondary" size="sm" className={problemHubStatusBadgeClass(status)}>
    {label}
  </Badge>
);

export const HubDetailPanel = () => {
  const { project } = useSwivuBryntumContext();
  const { selectedHub, setSelectedHub, setIsGenerateOpen } = useCoverage();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  if (!selectedHub) return null;


  const temporalCounts = TEMPORAL_PHASES.map((phase) => ({
    phase,
    count: selectedHub.temporalAssets[phase].length,
    assets: selectedHub.temporalAssets[phase],
  }));

  const handleOpenEdit = () => {
    setEditName(selectedHub.name);
    setEditDescription(selectedHub.description ?? "");
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedHub.record || !project) return;

    try {
      selectedHub.record.set("label", editName);
      selectedHub.record.set("description", editDescription || null);
      setIsEditOpen(false);
      await project.sync();
      toast.success(`Changes saved for "${editName}"`);
    } catch {
      toast.error("Failed to save changes.");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-card">
      {/* Header */}
      <div className="shrink-0 px-5 pt-4 pb-3.5 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-normal text-muted-foreground leading-none tracking-tight">Problem hub</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <h3 className="truncate text-base font-normal text-foreground leading-snug tracking-tight mt-1.5 cursor-default">
                  {selectedHub.name}
                </h3>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={6}
                className="max-w-[280px] space-y-1 px-3 py-2"
              >
                <p className="font-medium text-popover-foreground">{selectedHub.name}</p>
                {selectedHub.description && (
                  <p className="text-muted-foreground">{selectedHub.description}</p>
                )}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="shrink-0 flex items-center gap-1.5">
            <Button
              variant="default"
              className="h-7 gap-1.5 px-2.5 text-xs font-medium"
              onClick={() => setIsGenerateOpen(true)}
              title="Generate output"
            >
              <Sparkles className="size-3.5 shrink-0" />
              Generate
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-7"
              onClick={handleOpenEdit}
              title="Edit Hub"
            >
              <Pencil className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3.5 bg-background">
        {/* Temporal */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-muted-foreground">Temporal</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="What is temporal sequence?"
                >
                  <CircleHelp className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" align="start" sideOffset={6} className="max-w-[240px] text-left border">
                <p className="text-xs font-medium mb-1">Sequence readiness</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Tracks whether your hub has before, during, and after assets — the three temporal phases needed to produce credible sequence outputs like before/after comparisons or repair progressions.
                </p>
                <div className="mt-2 space-y-1 border-t border-border pt-2">
                  <p className="text-xs text-muted-foreground"><span className="font-medium text-success">Sequence ready</span> — all 3 phases present</p>
                  <p className="text-xs text-muted-foreground"><span className="font-medium text-primary">Before / After</span> — key pair covered, during missing</p>
                  <p className="text-xs text-muted-foreground"><span className="font-medium text-amber-500">Missing before / after</span> — incomplete pair</p>
                  <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground/60">No sequence</span> — no temporal data yet</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
          <Badge variant="secondary" size="sm" className={sequenceBadgeClassFromStatus(selectedHub.sequenceStatus)}>
            {SEQUENCE_STATUS_LABELS[selectedHub.sequenceStatus]}
          </Badge>
        </div>
        <div className="flex flex-col gap-1">
          {temporalCounts.map(({ phase, count, assets }) => (
            <SequencePhaseCollapsible
              key={phase}
              phase={phase}
              totalCount={count}
              present={count > 0}
              assets={assets}
            />
          ))}
        </div>

        {/* Content Roles */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-muted-foreground">Content roles</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="What are content roles?"
                  >
                    <CircleHelp className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" align="start" sideOffset={6} className="max-w-[240px] text-left border">
                  <p className="text-xs font-medium mb-1">Content strength</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Each content role is a required category of visual evidence (e.g. Wide, Defect, Workmanship). Hub strength reflects the percentage of roles that have at least one asset.
                  </p>
                  <div className="mt-2 space-y-1 border-t border-border pt-2">
                    <p className="text-xs text-muted-foreground"><span className="font-medium text-success">Strong</span> — 100% of roles filled</p>
                    <p className="text-xs text-muted-foreground"><span className="font-medium text-primary">Ready</span> — 80–99% of roles filled</p>
                    <p className="text-xs text-muted-foreground"><span className="font-medium text-amber-500">Partial</span> — 40–79% of roles filled</p>
                    <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground/60">Empty</span> — fewer than 40% of roles filled</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <StatusBadgeInline status={selectedHub.overallStatus} label={BUCKET_STATUS_LABELS[selectedHub.overallStatus]} />
          </div>
          <div className="flex flex-col gap-1">
            {selectedHub.buckets.map((bucket) => (
              <BucketCollapsible key={bucket.tagId} bucket={bucket} />
            ))}
          </div>
        </div>
      </div>

      {/* Edit Hub Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="gap-0 overflow-hidden rounded-xl border-border bg-card p-0 shadow-lg sm:max-w-md">
          <div className="border-b border-border bg-background/40 px-6 pt-6 pb-4">
            <DialogHeader className="space-y-0 text-left">
              <DialogTitle className="pr-8 text-lg font-semibold leading-tight tracking-tight">
                Edit Problem Hub
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed">
                Update the name and description for this problem hub.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-4 px-6 py-5">
            <div className="space-y-1.5">
              <label htmlFor="hub-edit-name" className="text-sm font-medium text-muted-foreground">
                Name
              </label>
              <Input
                id="hub-edit-name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="hub-edit-description" className="text-sm font-medium text-muted-foreground">
                What this covers
              </label>
              <Textarea
                id="hub-edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={4}
                placeholder="Describe the scope of this problem hub..."
                className="resize-y"
              />
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground/70 italic">
              Solution Tag connections are managed in Settings → Cross-Walk Mapping.
            </p>
          </div>

          <DialogFooter className="gap-2 border-t border-border bg-background/40 px-6 py-4 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SequencePhaseCollapsible = ({
  phase,
  totalCount,
  present,
  assets,
}: {
  phase: typeof TEMPORAL_PHASES[number];
  totalCount: number;
  present: boolean;
  assets: MarketingAsset[];
}) => {
  const [open, setOpen] = useState(false);
  const hasAssets = totalCount > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-transparent border-none cursor-pointer text-left transition-colors hover:bg-accent/50"
          >
            <SequenceIndicator passed={present} />
            <span className="flex-1 min-w-0 text-xs font-medium text-foreground capitalize">
              {phase}
            </span>
            <span className={cn("text-xs font-semibold min-w-3.5 text-right", hasAssets ? "text-foreground" : "text-amber-500/70")}>
              {totalCount}
            </span>
            <ChevronDown
              className={cn("shrink-0 size-3.5 text-muted-foreground/60 transition-transform", open && "rotate-180")}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-2.5 pt-2 pb-2.5 border-t border-border">
            {hasAssets ? (
              <div className="flex flex-wrap gap-1.5">
                {assets.map((asset) => (
                  <AssetThumbnail key={asset.id} asset={asset} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/60">
                No {phase} assets yet.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const SequenceIndicator = ({ passed }: { passed: boolean }) => (
  <div
    className={cn(
      "size-4 rounded-full shrink-0 flex items-center justify-center",
      passed ? "bg-success/15" : "bg-amber-500/15"
    )}
  >
    {passed ? (
      <Check className="size-2.5 text-success" />
    ) : (
      <CircleDashed className="size-2.5 text-amber-500/70" />
    )}
  </div>
);

const BucketCollapsible = ({ bucket }: { bucket: BucketFill }) => {
  const [open, setOpen] = useState(false);
  const hasAssets = bucket.assetCount > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 bg-transparent border-none cursor-pointer text-left transition-colors hover:bg-accent/50"
          >
            <SequenceIndicator passed={hasAssets} />
            <span className="flex-1 min-w-0 text-xs font-medium text-foreground">
              {bucket.label}
            </span>
            <span className={cn(
              "text-xs font-semibold min-w-3.5 text-right",
              hasAssets ? "text-success" : "text-amber-500/70"
            )}>
              {bucket.assetCount}
            </span>
            <ChevronDown
              className={cn("shrink-0 size-3.5 text-muted-foreground/60 transition-transform", open && "rotate-180")}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-2.5 pb-2.5 border-t border-border">
            {hasAssets ? (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {bucket.assets.map((asset) => (
                  <AssetThumbnail key={asset.id} asset={asset} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/60 mt-2">
                No assets in this role yet.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const AssetThumbnail = ({ asset }: { asset: MarketingAsset }) => {
  const { project } = useSwivuBryntumContext();
  const fileRecord = project?.boardFilesStore?.getById(asset.file) as any;
  const thumbnailUrl = fileRecord?.url || fileRecord?.thumbnailUrl;

  return (
    <div className="size-14 rounded-md border border-border bg-muted overflow-hidden shrink-0 cursor-pointer">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={asset.altText || asset.caption || ""}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="size-4 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};
